import { InputCheckbox } from '@creditornot/cb-checkbox';
import { DataTable } from '@creditornot/cb-data-table';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { setEnabledSftpConfigOfVenue } from 'apis/merchant-gateway/pos-integration-service/sftp-integration/api';
import { FileBasedIntegrationType } from 'apis/merchant-gateway/pos-integration-service/sftp-integration/types';
import { Typography as T } from 'common/typography';
import { localize } from 'common/utils';

import { useMerchantContext } from '../../merchant/MerchantContext';
import useVenuesSftpPosConfigs from '../hooks/useVenuesSftpPosConfigs';

type Props = {
  merchantId: string;
};

const Container = styled.div`
  margin-block-start: 16px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 26px 24px;
  color: ${cbSemanticCssVars.textPrimary};
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const LoadingSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-block-start: 40px;
  padding-block-end: 40px;
`;

type VenuesSftpConfig = {
  venue_id: string;
  name: string;
  item_update_csv_enabled: boolean;
  item_update_json_enabled: boolean;
  inventory_update_csv_enabled: boolean;
  inventory_update_json_enabled: boolean;
};

type VenueMap = { [venue_id: string]: VenuesSftpConfig };

const VenueSftpConfigManagement = ({ merchantId }: Props) => {
  const intl = useIntl();
  const { merchantVenues } = useMerchantContext();
  const { data: venuesWithSftpConfig, isLoading } = useVenuesSftpPosConfigs(merchantId);

  const initialData: VenueMap = useMemo(() => {
    return merchantVenues.reduce((acc, venue) => {
      acc[venue.id] = {
        name: localize(venue.name, intl.locale),
        item_update_csv_enabled: false,
        item_update_json_enabled: false,
        inventory_update_csv_enabled: false,
        inventory_update_json_enabled: false,
        venue_id: venue.id,
      };
      return acc;
    }, {} as VenueMap);
  }, [intl.locale, merchantVenues]);
  const [venueMap, setVenueMap] = useState<VenueMap>(initialData);

  useEffect(() => {
    if ((venuesWithSftpConfig?.length ?? 0) === 0) {
      return;
    }
    const onReceiveSftpConfig = (oldVenueMap: VenueMap) => {
      const updatedVenueMap: VenueMap = venuesWithSftpConfig.reduce((acc, venue) => {
        const updatedField = {
          ...oldVenueMap[venue.venue_id],
          item_update_csv_enabled: venue.item_update_csv_enabled,
          item_update_json_enabled: venue.item_update_json_enabled,
          inventory_update_csv_enabled: venue.inventory_update_csv_enabled,
          inventory_update_json_enabled: venue.inventory_update_json_enabled,
        };
        acc[venue.venue_id] = updatedField;
        return acc;
      }, {} as VenueMap);

      return {
        ...oldVenueMap,
        ...updatedVenueMap,
      };
    };

    setVenueMap(onReceiveSftpConfig);
  }, [venuesWithSftpConfig, setVenueMap]);

  const handleAccessChange = (v: VenuesSftpConfig, type: FileBasedIntegrationType) => {
    const newValue = Object.assign({}, { ...v });
    let isEnabled = false;
    switch (type) {
      case FileBasedIntegrationType.INVENTORY_UPDATE_CSV: {
        isEnabled = !v.inventory_update_csv_enabled;
        newValue.inventory_update_csv_enabled = isEnabled;
        break;
      }
      case FileBasedIntegrationType.INVENTORY_UPDATE_JSON: {
        isEnabled = !v.inventory_update_json_enabled;
        newValue.inventory_update_json_enabled = isEnabled;
        break;
      }
      case FileBasedIntegrationType.ITEM_UPDATE_CSV: {
        isEnabled = !v.item_update_csv_enabled;
        newValue.item_update_csv_enabled = isEnabled;
        break;
      }
      case FileBasedIntegrationType.ITEM_UPDATE_JSON: {
        isEnabled = !v.item_update_json_enabled;
        newValue.item_update_json_enabled = isEnabled;
        break;
      }
    }
    setEnabledSftpConfigOfVenue(merchantId, {
      venue_id: v.venue_id,
      enabled: isEnabled,
      type,
    });

    setVenueMap({
      ...venueMap,
      ...{ [v.venue_id]: newValue },
    });
  };

  return (
    <>
      <Container>
        <Header>
          <T.Heading6>
            <FormattedMessage id="merchant-integrations.sftp.venue-mgmt.heading" />
          </T.Heading6>
        </Header>
        {isLoading && (
          <LoadingSpinnerContainer>
            <Spinner size="large" />
          </LoadingSpinnerContainer>
        )}
        {!isLoading && (
          <DataTable
            dataSource={Object.values(venueMap)}
            getRowKey={record => record.name}
            horizontalPadding="wide"
            columns={[
              {
                id: 'name',
                name: intl.formatMessage({ id: 'merchant-integrations.venue-name' }),
                render: ({ record }) => {
                  return <>{record.name}</>;
                },
              },
              {
                id: 'id',
                name: intl.formatMessage({ id: 'merchant-integrations.venue-id' }),
                render: ({ record }) => {
                  return <>{record.venue_id}</>;
                },
              },
              {
                id: 'invUpdCsv',
                name: intl.formatMessage({ id: 'merchant-integrations.sftp.venue-mgmt.inventory-update-csv' }),
                render: ({ record }) => {
                  return (
                    <InputCheckbox
                      checked={record.inventory_update_csv_enabled}
                      onChange={() => handleAccessChange(record, FileBasedIntegrationType.INVENTORY_UPDATE_CSV)}
                    />
                  );
                },
              },
              {
                id: 'invUpdJson',
                name: intl.formatMessage({ id: 'merchant-integrations.sftp.venue-mgmt.inventory-update-json' }),
                render: ({ record }) => {
                  return (
                    <InputCheckbox
                      checked={record.inventory_update_json_enabled}
                      onChange={() => handleAccessChange(record, FileBasedIntegrationType.INVENTORY_UPDATE_JSON)}
                    />
                  );
                },
              },
              {
                id: 'itemUpdCsv',
                name: intl.formatMessage({ id: 'merchant-integrations.sftp.venue-mgmt.item-update-csv' }),
                render: ({ record }) => {
                  return (
                    <InputCheckbox
                      checked={record.item_update_csv_enabled}
                      onChange={() => handleAccessChange(record, FileBasedIntegrationType.ITEM_UPDATE_CSV)}
                    />
                  );
                },
              },
              {
                id: 'itemUpdJson',
                name: intl.formatMessage({ id: 'merchant-integrations.sftp.venue-mgmt.item-update-json' }),
                render: ({ record }) => {
                  return (
                    <InputCheckbox
                      checked={record.item_update_json_enabled}
                      onChange={() => handleAccessChange(record, FileBasedIntegrationType.ITEM_UPDATE_JSON)}
                    />
                  );
                },
              },
            ]}
          />
        )}
      </Container>
    </>
  );
};

export default VenueSftpConfigManagement;
